import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import type { ArTimelineCatalyst, ArTimelineRow } from '../../types';
import {
  TIER_NAME, rowMod, phaseNorm, PHASES, parseDate, regLabel, ST_LABEL, firstAgent, sponsorGroup,
} from './arHelpers';
import type { ArSharedProps } from './AssetRadarPage';

type GroupMode = 'tier' | 'asset' | 'none';
type SortMode = 'pc' | 'catalyst' | 'sponsor' | 'phase';

// §2 bar colour = ClinicalTrials.gov status (asset_radar/timeline.js BAR_ST); phase is a badge only, never a bar colour.
const BAR_ST: Record<string, string> = {
  RECRUITING: 'go', ENROLLING_BY_INVITATION: 'go', NOT_YET_RECRUITING: 'soon', ACTIVE_NOT_RECRUITING: 'active',
  COMPLETED: 'done', SUSPENDED: 'susp', TERMINATED: 'stop', WITHDRAWN: 'stop',
};
const barKey = (status?: string | null) => (status && BAR_ST[status]) || 'na';
const PH_BADGE: Record<string, string> = { '1': 'Ph1', '12': 'Ph1/2', '2': 'Ph2', '23': 'Ph2/3', '3': 'Ph3', '4': 'Approved' };
const phBucket = (p: string): string => {
  const t = (p || '').trim();
  if (/approv|ph(?:ase)?\s*4|\biv\b/i.test(t)) return '4';
  const d = [...new Set((t.match(/[1-3]/g) || []).map(Number))].sort();
  if (!d.length) return 'na';
  if (d.length > 1) return d[d.length - 1] === 3 ? '23' : '12';
  return String(d[0]);
};
const CAT_STATE: Record<string, string> = { pos: 'Positive', appr: 'Approved', neg: 'Negative / CRL', mixed: 'Mixed', occ: 'Occurred, not scored', exp: 'Expected', late: 'Delayed or overdue' };
function catState(k: ArTimelineCatalyst, d: Date | null): string {
  const o = (k.outcome || '').toLowerCase();
  if (o && o !== 'pending') {
    if (/neg|crl|fail|miss/.test(o)) return 'neg';
    if (/approv/.test(o)) return 'appr';
    if (/mix/.test(o)) return 'mixed';
    if (/pos|success|met|hit/.test(o)) return 'pos';
    return 'occ';
  }
  if (k.delayed) return 'late';
  if (d && d < new Date(new Date().setHours(0, 0, 0, 0))) return 'occ';
  return 'exp';
}
const isHigh = (impact?: string | null) => /high/i.test(impact || '');
const MONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const mon = (d: Date) => `${MONS[d.getMonth()]} ’${String(d.getFullYear()).slice(2)}`;

function rowDates(r: ArTimelineRow): { start: Date | null; pc: Date | null; sc: Date | null } {
  const start = r.reg?.start_date ? parseDate(r.reg.start_date) : null;
  const pc = r.reg?.primary_completion_date ? parseDate(r.reg.primary_completion_date) : (r.workbook_date ? parseDate(r.workbook_date) : null);
  const sc = r.reg?.completion_date ? parseDate(r.reg.completion_date) : null;
  return { start, pc, sc };
}

interface Filters {
  tiers: Set<string>; mods: Set<string>; phases: Set<string>; statuses: Set<string>; spons: Set<string>; rs: Set<string>; catOnly: boolean; q: string;
}
const emptyFilters = (): Filters => ({ tiers: new Set(), mods: new Set(), phases: new Set(), statuses: new Set(), spons: new Set(), rs: new Set(), catOnly: false, q: '' });

const TimelineTab: React.FC<ArSharedProps> = ({ disease, ind, tracker }) => {
  const [params] = useSearchParams();
  const [rows, setRows] = useState<ArTimelineRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(() => ({ ...emptyFilters(), q: params.get('q') || '' }));
  const [group, setGroup] = useState<GroupMode>('tier');
  const [sort, setSort] = useState<SortMode>('pc');
  const [roomy, setRoomy] = useState(false);
  const [legendHidden, setLegendHidden] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [mode, setMode] = useState<'gantt' | 'quarter'>('gantt');
  const [showPast, setShowPast] = useState(false);

  const fileId = disease?.versions?.[0]?.file_id;

  useEffect(() => {
    if (!disease || !ind) return;
    let cancelled = false;
    // Clearing rows (not just setting loading=true) is what makes the "if (loading && !rows)" spinner
    // below actually show on every indication/setting switch -- without this, switching away from an
    // already-loaded timeline left the OLD indication's rows on screen with no visible sign a fetch was
    // in progress, which is exactly what looked like "nothing happening" on a slow (0.5-5s) backend call.
    setRows(null);
    setLoading(true); setError(null);
    apiService.arGetTimeline(disease.key, ind.key, fileId).then((res) => { if (!cancelled) setRows((res.rows || []).filter((r) => /^NCT\d{8}$/i.test(r.nct || ''))); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Could not load the timeline'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [disease?.key, ind?.key, fileId]);

  const sponOf = (r: ArTimelineRow) => sponsorGroup(r.reg?.sponsor || r.sponsor);

  const passBase = (r: ArTimelineRow) => {
    if (filters.tiers.size && !filters.tiers.has(String(r.tier))) return false;
    if (filters.mods.size && !filters.mods.has(rowMod(tracker, r))) return false;
    if (filters.phases.size && !filters.phases.has(phaseNorm(r.reg?.phase, r.phase))) return false;
    if (filters.statuses.size && !filters.statuses.has(r.st)) return false;
    if (filters.q) {
      const t = `${r.sponsor} ${r.trial} ${r.regimen} ${r.nct} ${r.asset}`.toLowerCase();
      if (!t.includes(filters.q.toLowerCase())) return false;
    }
    return true;
  };
  const passReg = (r: ArTimelineRow) => !filters.rs.size || filters.rs.has(r.reg ? regLabel(r.reg.status) : 'Not in registry');
  const passSpon = (r: ArTimelineRow) => !filters.spons.size || filters.spons.has(sponOf(r));

  const tierOpts = useMemo(() => { if (!rows) return []; const s = new Set(rows.map((r) => r.tier)); return [1, 2, 3].filter((t) => s.has(t as 1 | 2 | 3)); }, [rows]);
  const modOpts = useMemo(() => {
    if (!rows) return [] as [string, number][];
    const m = new Map<string, number>();
    rows.forEach((r) => { const k = rowMod(tracker, r); m.set(k, (m.get(k) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [rows, tracker]);
  const phaseOpts = useMemo(() => {
    if (!rows) return [] as string[];
    const counts = new Map<string, number>();
    rows.forEach((r) => { const p = phaseNorm(r.reg?.phase, r.phase); if (p) counts.set(p, (counts.get(p) || 0) + 1); });
    return PHASES.filter((p) => counts.has(p));
  }, [rows]);
  const statusOpts = useMemo(() => { if (!rows) return [] as ('mature' | 'early' | 'none')[]; const s = new Set(rows.map((r) => r.st)); return (['mature', 'early', 'none'] as const).filter((k) => s.has(k)); }, [rows]);
  const sponsorCounts = useMemo(() => {
    if (!rows) return [] as [string, number][];
    const m = new Map<string, number>();
    rows.filter((r) => passBase(r) && passReg(r)).forEach((r) => { const k = sponOf(r); if (k) m.set(k, (m.get(k) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filters.tiers, filters.phases, filters.statuses, filters.q]);
  const rsOrder = ['Recruiting', 'Not yet recruiting', 'Enrolling by invitation', 'Active, not recruiting', 'Completed', 'Suspended', 'Terminated', 'Withdrawn', 'Unknown status', 'Not in registry'];
  const rsCounts = useMemo(() => {
    if (!rows) return [] as [string, number][];
    const m = new Map<string, number>();
    rows.filter(passBase).forEach((r) => { const k = r.reg ? regLabel(r.reg.status) : 'Not in registry'; m.set(k, (m.get(k) || 0) + 1); });
    return [...m.entries()].sort((a, b) => rsOrder.indexOf(a[0]) - rsOrder.indexOf(b[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filters.tiers, filters.phases, filters.statuses, filters.q]);

  const filtered = useMemo(() => (rows || []).filter((r) => passBase(r) && passReg(r) && passSpon(r)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, filters]);
  const catRows = useMemo(() => new Map(filtered.filter((r) => r.catalysts.length).map((r) => [r.nct, r.catalysts])), [filtered]);
  const shown = filters.catOnly ? filtered.filter((r) => catRows.has(r.nct)) : filtered;

  const filterCount = filters.tiers.size + filters.mods.size + filters.phases.size + filters.statuses.size + filters.spons.size + filters.rs.size + (filters.catOnly ? 1 : 0);
  const clearAll = () => setFilters(emptyFilters());

  if (loading && !rows) return <div className="state"><div className="spin" /><p>Loading database…</p></div>;
  if (error) return <div className="state"><h2>Timeline couldn&rsquo;t be loaded</h2><p>{error}</p></div>;
  if (rows && !rows.length) {
    return <div className="state"><h2>No trials with an NCT for {ind?.label}</h2><p>The timeline shows registry trials only; trials without an NCT stay on the Landscape tab.</p></div>;
  }

  const activeChips: [string, () => void][] = [];
  filters.tiers.forEach((t) => activeChips.push([`Tier: ${TIER_NAME[Number(t)]}`, () => setFilters((f) => { const n = new Set(f.tiers); n.delete(t); return { ...f, tiers: n }; })]));
  filters.mods.forEach((m) => activeChips.push([`Modality: ${m}`, () => setFilters((f) => { const n = new Set(f.mods); n.delete(m); return { ...f, mods: n }; })]));
  filters.phases.forEach((p) => activeChips.push([`Phase: ${p}`, () => setFilters((f) => { const n = new Set(f.phases); n.delete(p); return { ...f, phases: n }; })]));
  filters.statuses.forEach((s) => activeChips.push([`Data: ${ST_LABEL[s] || s}`, () => setFilters((f) => { const n = new Set(f.statuses); n.delete(s); return { ...f, statuses: n }; })]));
  filters.rs.forEach((k) => activeChips.push([`Recruitment: ${k}`, () => setFilters((f) => { const n = new Set(f.rs); n.delete(k); return { ...f, rs: n }; })]));
  filters.spons.forEach((k) => activeChips.push([`Sponsor: ${k}`, () => setFilters((f) => { const n = new Set(f.spons); n.delete(k); return { ...f, spons: n }; })]));
  if (filters.catOnly) activeChips.push(['Only trials with catalysts', () => setFilters((f) => ({ ...f, catOnly: false }))]);

  const toolbar = (controls: React.ReactNode, count: string) => (
    <div className="tl-bar">
      {controls && <div className="tl-left">{controls}</div>}
      <div className="tl-right">
        {count && <span className="meta g-count">{count}</span>}
        <button type="button" className="btn tl-key tl-keyicon" aria-pressed={!legendHidden} aria-label={legendHidden ? 'Show key' : 'Hide key'} title={legendHidden ? 'Show key' : 'Hide key'} onClick={() => setLegendHidden((v) => !v)}>
          <svg viewBox="0 0 16 16" width={14} height={14} aria-hidden="true"><path d="M10.5 1.5a4 4 0 0 0-3.9 4.9L1.5 11.5V14.5h3v-1.5H6v-1.5h1.5l1.1-1.1A4 4 0 1 0 10.5 1.5zm1 2.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" fill="currentColor" /></svg>
        </button>
        <input className="search tl-find" type="search" placeholder="Sponsor, trial, regimen or NCT" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} aria-label="Find trials" />
        <div className="tl-fwrap">
          <button type="button" className={`btn tl-fbtn${filterCount ? ' on' : ''}`} aria-haspopup="dialog" aria-expanded={filterOpen} onClick={() => setFilterOpen((v) => !v)}>
            Filters {filterCount > 0 && <span className="tl-badge" aria-label={`${filterCount} active`}>{filterCount}</span>}
          </button>
          {filterOpen && (
            <div className="tl-fpop" role="dialog" aria-label="Filters">
              {tierOpts.length > 0 && (
                <div className="frow" role="group" aria-label="Tier">
                  <span className="fl cap">Tier</span>
                  {tierOpts.map((t) => (
                    <button key={t} type="button" className="chip" aria-pressed={filters.tiers.has(String(t))} onClick={() => setFilters((f) => { const n = new Set(f.tiers); n.has(String(t)) ? n.delete(String(t)) : n.add(String(t)); return { ...f, tiers: n }; })}>
                      <span className="sw" style={{ background: `var(--t${t})` }} />{TIER_NAME[t]}
                    </button>
                  ))}
                </div>
              )}
              {modOpts.length > 0 && (
                <div className="frow" role="group" aria-label="Modality">
                  <span className="fl cap">Modality</span>
                  {modOpts.map(([m, n]) => (
                    <button key={m} type="button" className="chip" aria-pressed={filters.mods.has(m)} onClick={() => setFilters((f) => { const s = new Set(f.mods); s.has(m) ? s.delete(m) : s.add(m); return { ...f, mods: s }; })}>{m} ({n})</button>
                  ))}
                </div>
              )}
              {phaseOpts.length > 0 && (
                <div className="frow" role="group" aria-label="Phase">
                  <span className="fl cap">Phase</span>
                  {phaseOpts.map((p) => (
                    <button key={p} type="button" className="chip" aria-pressed={filters.phases.has(p)} onClick={() => setFilters((f) => { const n = new Set(f.phases); n.has(p) ? n.delete(p) : n.add(p); return { ...f, phases: n }; })}>{p}</button>
                  ))}
                </div>
              )}
              {statusOpts.length > 0 && (
                <div className="frow" role="group" aria-label="Data status">
                  <span className="fl cap">Data status</span>
                  {statusOpts.map((s) => (
                    <button key={s} type="button" className="chip" aria-pressed={filters.statuses.has(s)} onClick={() => setFilters((f) => { const n = new Set(f.statuses); n.has(s) ? n.delete(s) : n.add(s); return { ...f, statuses: n }; })}>{ST_LABEL[s]}</button>
                  ))}
                </div>
              )}
              {sponsorCounts.length > 0 && (
                <div className="tl-spon">
                  <div className="frow" role="group" aria-label="Sponsor">
                    <span className="fl cap">Sponsor</span>
                    {sponsorCounts.slice(0, 20).map(([k, n]) => (
                      <button key={k} type="button" className="chip" aria-pressed={filters.spons.has(k)} onClick={() => setFilters((f) => { const s = new Set(f.spons); s.has(k) ? s.delete(k) : s.add(k); return { ...f, spons: s }; })}>{k} ({n})</button>
                    ))}
                  </div>
                </div>
              )}
              {rsCounts.length > 0 && (
                <div className="frow" role="group" aria-label="Recruitment">
                  <span className="fl cap">Recruitment</span>
                  {rsCounts.map(([k, n]) => (
                    <button key={k} type="button" className="chip" aria-pressed={filters.rs.has(k)} onClick={() => setFilters((f) => { const s = new Set(f.rs); s.has(k) ? s.delete(k) : s.add(k); return { ...f, rs: s }; })}>{k} ({n})</button>
                  ))}
                </div>
              )}
              <div className="frow" role="group" aria-label="Catalysts">
                <span className="fl cap">Catalysts</span>
                <button type="button" className="chip" aria-pressed={filters.catOnly} title="Show only trials with an upcoming catalyst" onClick={() => setFilters((f) => ({ ...f, catOnly: !f.catOnly }))}>
                  <span className="g-cdot" aria-hidden="true" /> Only trials with catalysts
                </button>
              </div>
              <div className="dd-foot">
                <button type="button" className="linkbtn" disabled={!filterCount} onClick={clearAll}>Clear all</button>
                <button type="button" className="linkbtn" onClick={() => setFilterOpen(false)}>Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="g-frame">
      {toolbar(null, '')}
      {activeChips.length > 0 && (
        <div className="tl-chips" role="group" aria-label="Active filters">
          {activeChips.map(([l, fn]) => (
            <button key={l} type="button" className="chip tl-achip" aria-label={`Remove filter ${l}`} onClick={fn}>{l} <span className="tl-x" aria-hidden="true">&times;</span></button>
          ))}
          <button type="button" className="linkbtn" onClick={clearAll}>Clear all</button>
        </div>
      )}
      <div className="tl-bar">
        <div className="tl-left">
          <div className="g-ctl"><span className="cap tok">Group</span>
            <div className="seg" role="group" aria-label="Group">
              {(['tier', 'asset', 'none'] as const).map((k) => (
                <button key={k} type="button" aria-pressed={group === k} onClick={() => setGroup(k)} style={{ textTransform: 'capitalize' }}>{k}</button>
              ))}
            </div>
          </div>
          <div className="g-ctl"><span className="cap tok">Sort</span>
            <select className="pill" style={{ width: 170 }} value={sort} onChange={(e) => setSort(e.target.value as SortMode)} aria-label="Sort rows">
              <option value="pc">Primary completion</option>
              <option value="catalyst">Next catalyst</option>
              <option value="sponsor">Sponsor</option>
              <option value="phase">Phase</option>
            </select>
          </div>
          <div className="g-ctl"><span className="cap tok">Rows</span>
            <div className="seg" role="group" aria-label="Rows">
              <button type="button" aria-pressed={!roomy} onClick={() => setRoomy(false)}>Compact</button>
              <button type="button" aria-pressed={roomy} onClick={() => setRoomy(true)}>Roomy</button>
            </div>
          </div>
          <div className="seg" role="group" aria-label="Layout">
            <button type="button" aria-pressed={mode === 'gantt'} onClick={() => setMode('gantt')}>Gantt</button>
            <button type="button" aria-pressed={mode === 'quarter'} onClick={() => setMode('quarter')}>By quarter</button>
          </div>
        </div>
      </div>

      {!legendHidden && (
        <div className="g-legend" role="note" aria-label="Key">
          <span className="g-lg-grp"><b>Bar = registry status</b>
            {[['go', 'Recruiting'], ['soon', 'Not yet recruiting'], ['active', 'Active, not recruiting'], ['done', 'Completed'], ['susp', 'Suspended'], ['stop', 'Terminated / withdrawn'], ['na', 'Not in registry']].map(([k, l]) => (
              <span key={k} className="g-lg-it"><i className={`g-sw bs-${k}`} />{l}</span>
            ))}
          </span>
          <span className="g-lg-grp"><b>Milestones</b>
            <span className="g-lg-it"><i className="g-lg-start" />Start</span>
            <span className="g-lg-it"><i className="g-lg-pc" />Primary completion</span>
            <span className="g-lg-it"><i className="g-lg-fc" />To final completion</span>
            <span className="g-lg-it"><span className="g-lg-edge">{'◂ ▸'}</span>Beyond the axis</span>
          </span>
          <span className="g-lg-grp"><b>Catalysts</b>
            {['pos', 'appr', 'neg', 'mixed', 'occ', 'exp', 'late'].map((s) => <span key={s} className="g-lg-it"><i className={`g-lg-cat cs-${s}`} />{CAT_STATE[s]}</span>)}
          </span>
          <span className="g-lg-grp"><span className="g-lg-it"><i className="g-lg-today" />Today</span></span>
        </div>
      )}

      {!shown.length ? (
        <div className="state">
          <h2>No trials match these filters</h2>
          <button type="button" className="btn" onClick={clearAll}>Clear filters</button>
        </div>
      ) : mode === 'quarter' ? (
        <Quarters rows={shown} />
      ) : (
        <Gantt
          rows={shown} group={group} sort={sort} roomy={roomy} showPast={showPast} setShowPast={setShowPast}
          resetKey={`${disease?.key || ''}|${ind?.key || ''}`}
        />
      )}
    </div>
  );
};

/* ---------- Gantt ---------- */
const Gantt: React.FC<{
  rows: ArTimelineRow[]; group: GroupMode; sort: SortMode; roomy: boolean;
  showPast: boolean; setShowPast: (v: boolean) => void; resetKey: string;
}> = ({ rows, group, sort, roomy, showPast, setShowPast, resetKey }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const now = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const nowT = now.getTime();
  const DAY = 864e5;

  const info = useMemo(() => new Map(rows.map((r) => [r, rowDates(r)])), [rows]);
  const catsOf = (r: ArTimelineRow) => r.catalysts.map((k) => ({ k, d: parseDate(k.date) || parseDate(k.window) })).filter((x) => x.d).sort((a, b) => (a.d as Date).getTime() - (b.d as Date).getTime());
  const nextCat = (r: ArTimelineRow) => catsOf(r).find((x) => (x.d as Date).getTime() >= nowT) || null;
  const pcOf = (r: ArTimelineRow) => info.get(r)?.pc || null;
  const isPast = (r: ArTimelineRow) => { const v = info.get(r)!; return !!v.pc && v.pc.getTime() < nowT && !(v.sc && v.sc.getTime() >= nowT) && !catsOf(r).length; };

  const ts: number[] = [];
  info.forEach((v) => { if (v.pc) ts.push(v.pc.getTime()); if (v.sc) ts.push(v.sc.getTime()); if (v.start) ts.push(v.start.getTime()); });
  rows.forEach((r) => catsOf(r).forEach((x) => ts.push((x.d as Date).getTime())));
  const qStart = (t: number) => { const d = new Date(t); d.setDate(1); d.setMonth(Math.floor(d.getMonth() / 3) * 3); d.setHours(0, 0, 0, 0); return d; };
  const FLOOR = new Date(2020, 0, 1).getTime(), CAP = nowT + 3650 * DAY;
  const lo = qStart(Math.max(FLOOR, Math.min(nowT - 183 * DAY, ...(ts.length ? ts : [nowT]))));
  const hi = qStart(Math.min(CAP, Math.max(nowT + 400 * DAY, ...(ts.length ? ts : [nowT]))));
  hi.setMonth(hi.getMonth() + 3);
  const min = lo.getTime(), max = hi.getTime();
  const quarters: Date[] = []; for (let q = new Date(lo); q < hi; q.setMonth(q.getMonth() + 3)) quarters.push(new Date(q));
  const years: number[] = []; for (let y = lo.getFullYear(); y <= hi.getFullYear(); y++) years.push(y);

  const tw = Math.max(900, quarters.length * 50);
  const pos = (t: number) => ((t - min) / (max - min)) * 100;
  const todayP = pos(nowT);
  const qpx = tw / quarters.length;

  // Default the view to Today instead of the start of the axis (which can run back to 2020 or 6 months
  // before now) -- re-anchored only when the disease/setting actually changes, not on every filter tweak.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = Math.max(0, (todayP / 100) * tw - 80);
    el.scrollTop = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- anchor once per resetKey, not on every todayP/tw change
  }, [resetKey]);

  const phaseRank = (r: ArTimelineRow) => { const m = (r.phase || '').match(/[0-4]/); return /approv/i.test(r.phase || '') ? 5 : (m ? +m[0] : 0); };
  const sponOf = (r: ArTimelineRow) => sponsorGroup(r.reg?.sponsor || r.sponsor);
  type RowCmp = (a: ArTimelineRow, b: ArTimelineRow) => number;
  const sortFns: Record<SortMode, RowCmp> = {
    pc: (a, b) => (pcOf(a)?.getTime() || Infinity) - (pcOf(b)?.getTime() || Infinity),
    catalyst: (a, b) => { const x = nextCat(a), y = nextCat(b); return ((x?.d as Date | undefined)?.getTime() ?? Infinity) - ((y?.d as Date | undefined)?.getTime() ?? Infinity) || (pcOf(a)?.getTime() || Infinity) - (pcOf(b)?.getTime() || Infinity); },
    sponsor: (a, b) => sponOf(a).localeCompare(sponOf(b)) || (pcOf(a)?.getTime() || Infinity) - (pcOf(b)?.getTime() || Infinity),
    phase: (a, b) => phaseRank(b) - phaseRank(a) || (pcOf(a)?.getTime() || Infinity) - (pcOf(b)?.getTime() || Infinity),
  };
  const sortFn = sortFns[sort];

  const activeAll = rows.filter((r) => pcOf(r) && !isPast(r));
  const pastRows = rows.filter((r) => pcOf(r) && isPast(r)).sort((a, b) => (pcOf(b)?.getTime() || 0) - (pcOf(a)?.getTime() || 0));
  const undated = rows.filter((r) => !pcOf(r));

  const assetOf = (r: ArTimelineRow) => (r.asset || firstAgent(r.regimen || r.trial) || 'Unlabeled').slice(0, 42);
  type Group = { label: string; color?: string; rows: ArTimelineRow[] };
  let groups: Group[];
  if (group === 'asset') {
    const m = new Map<string, ArTimelineRow[]>();
    activeAll.forEach((r) => { const k = assetOf(r); if (!m.has(k)) m.set(k, []); m.get(k)!.push(r); });
    groups = [...m.entries()].map(([k, rs]) => ({ label: k, rows: rs.slice().sort(sortFn) }));
  } else if (group === 'none') {
    groups = activeAll.length ? [{ label: 'All trials', rows: activeAll.slice().sort(sortFn) }] : [];
  } else {
    groups = ([1, 2, 3] as const).map((t) => ({ label: TIER_NAME[t], color: `var(--t${t})`, rows: activeAll.filter((r) => r.tier === t).sort(sortFn) })).filter((g) => g.rows.length);
  }

  const H = roomy ? 64 : 44, BAR = 8, cy = roomy ? 30 : 28, barTop = cy - BAR / 2;
  const yearLines = () => years.map((y) => { const t = new Date(y, 0, 1).getTime(); return t > min && t < max ? <div key={y} className="g-yr2" style={{ left: `${pos(t)}%` }} /> : null; });

  const Row: React.FC<{ r: ArTimelineRow; pastRow?: boolean }> = ({ r, pastRow }) => {
    const { start, pc, sc } = info.get(r)!;
    const bk = barKey(r.reg?.status);
    const cats = catsOf(r).filter((x) => (x.d as Date).getTime() >= min && (x.d as Date).getTime() <= max);
    const ph = PH_BADGE[phBucket(r.phase || r.reg?.phase || '')];
    const reg = r.reg;
    const n = nextCat(r);
    const hiCat = cats.find((x) => isHigh(x.k.impact) && catState(x.k, x.d) === 'exp');
    const DS: Record<string, string> = { mature: 'Mature', early: 'Early', none: 'No data yet' };
    const openNct = () => { if (r.nct) window.open(`https://clinicaltrials.gov/study/${r.nct}`, '_blank', 'noopener'); };
    return (
      <div
        className={`g-row${pastRow ? ' past' : ''}`}
        tabIndex={0}
        role="link"
        aria-label={`${r.trial || r.nct}. Open on ClinicalTrials.gov`}
        style={r.nct ? { cursor: 'pointer' } : undefined}
        onClick={openNct}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openNct(); } }}
      >
        <button
          type="button" className="g-lab g-lab2"
          title={`${r.trial || r.nct}\n${[r.phase, r.sponsor].filter(Boolean).join(' · ')}${r.nct ? '\n' + r.nct : ''}${n ? `\nNext catalyst: ${n.k.date || n.k.window || ''} · ${n.k.type || 'Catalyst'}` : ''}${r.nct ? '\n\nOpen on ClinicalTrials.gov' : ''}`}
        >
          <b><span className="g-mq">{r.trial || r.nct || 'Unnamed trial'}</span></b>
          <small className="g-l2">
            {r.source !== 'landscape' && <span className="g-bdg g-src" title={r.source === 'registry' ? 'Related ongoing registry study, queued for the next landscape refresh' : 'From the Catalyst Tracker, queued for landscape research'}>{r.source === 'registry' ? 'Registry' : 'Tracker'}</span>}
            {ph && <span className="g-bdg">{ph}</span>}
            {DS[r.st] && <span className="g-bdg">{DS[r.st]}</span>}
            {hiCat && <span className="g-hi">High impact</span>}
            <span className="g-l2t">{r.sponsor || reg?.sponsor || 'Sponsor NR'}</span>
          </small>
          {roomy && <small className="g-l3">{[r.nct, reg ? regLabel(reg.status) : '', reg?.enrollment != null ? `N=${Number(reg.enrollment).toLocaleString()}` : ''].filter(Boolean).join(' · ')}</small>}
        </button>
        <div className={`g-track2 bs-${bk}`} style={{ height: H }}>
          <div className="g-pastshade" style={{ width: `${Math.max(0, Math.min(100, todayP))}%` }} />
          {yearLines()}
          <div className="g-today2" style={{ left: `${todayP}%` }} />
          {!pc && !start && (
            <div className={`g-nodate${r.reg_state === 'pending' ? ' loading' : ''}`} style={{ top: barTop - 5 }}><span style={{ left: `${todayP}%` }}>{r.reg_state === 'pending' ? 'loading dates…' : 'date not reported'}</span></div>
          )}
          {pc && (() => {
            const bs = start && start < pc ? start : (pc.getTime() > nowT ? now : null);
            const kids: React.ReactNode[] = [];
            if (bs) {
              const l = Math.max(bs.getTime(), min), rr = Math.min(pc.getTime(), max);
              if (rr > l) {
                const segs: [number, number, string][] = (nowT > l && nowT < rr) ? [[l, nowT, ' past seg-l'], [nowT, rr, ' seg-r']] : [[l, rr, rr <= nowT ? ' past' : '']];
                segs.forEach(([a, b, k], i) => kids.push(<div key={`bar${i}`} className={`g-bar2${k}`} title={`${r.trial || r.nct}\n${regLabel(r.reg?.status)}\nStart ${start ? mon(start) : 'NR'} → primary completion ${mon(pc)}`} style={{ left: `${pos(a)}%`, width: `${pos(b) - pos(a)}%`, top: barTop }} />));
              }
            }
            if (start && start.getTime() >= min && start.getTime() <= max) kids.push(<div key="st" className="g-st" style={{ left: `${pos(start.getTime())}%`, top: cy - 7 }} />);
            else if (start && start.getTime() < min) kids.push(<div key="edgeL" className="g-edge l" style={{ top: cy - 8 }}>{'◂'} {start.getFullYear()}</div>);
            if (sc && sc > pc) {
              const l = Math.max(pc.getTime(), min), rr = Math.min(sc.getTime(), max);
              if (rr > l) kids.push(<div key="tail" className="g-tail" style={{ left: `${pos(l)}%`, width: `${pos(rr) - pos(l)}%`, top: cy - 1 }} />);
            }
            if (pc.getTime() >= min && pc.getTime() <= max) kids.push(<div key="pcm" className="g-pcm" title={`Primary completion ${mon(pc)}`} style={{ left: `${pos(pc.getTime())}%`, top: cy - 5 }} />);
            const endT = (sc && sc > pc ? sc : pc).getTime();
            if (endT > max) kids.push(<div key="edgeR" className="g-edge r" style={{ top: cy - 8 }}>{new Date(endT).getFullYear()} {'▸'}</div>);
            return kids;
          })()}
          {cats.map((c, i) => {
            const st = catState(c.k, c.d);
            const label = `${c.k.date || c.k.window || ''} · ${c.k.type || 'Catalyst'}: ${c.k.event || ''}`;
            return (
              <div
                key={i} className={`g-ch cs-${st}`} role="link" tabIndex={0}
                aria-label={`${label}. ${CAT_STATE[st]}`} title={`${label}\n${CAT_STATE[st]}${c.k.venue ? '\n' + c.k.venue : ''}`}
                style={{ left: `${pos((c.d as Date).getTime())}%`, top: cy }}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const grpHead = (label: string, color: string | undefined, n: number, extra?: React.ReactNode) => (
    <div className="g-group g-group2">
      {color && <i className="g-gsw" style={{ background: color }} />}
      <span className="g-gl">{label}</span>
      <span className="g-gn">{n}</span>
      {extra}
    </div>
  );

  return (
    <div ref={scrollRef} className={`gantt g2${roomy ? ' roomy' : ''}`} style={{ '--gw': `${tw}px`, '--lw': '320px' } as React.CSSProperties}>
      <div className="g-head">
        <div className="gl cap">Trial</div>
        <div className="g-axis2">
          <div className="g-ax-past" style={{ width: `${Math.max(0, Math.min(100, todayP))}%` }} />
          {years.map((y) => { const t = Math.max(min, new Date(y, 0, 1).getTime()); if (t >= max) return null; return <div key={y} className="g-ax-y" style={{ left: `${pos(t)}%` }}><span>{y}</span></div>; })}
          {quarters.map((q, i) => <div key={i} className="g-ax-q" style={{ left: `${pos(q.getTime())}%` }}>{qpx >= 44 && <span>{`Q${Math.floor(q.getMonth() / 3) + 1} ’${String(q.getFullYear()).slice(2)}`}</span>}</div>)}
          <span className="g-ax-today" style={{ left: `${todayP}%` }}>Today</span>
        </div>
      </div>
      {groups.map((g, i) => (
        <React.Fragment key={i}>
          {grpHead(g.label, g.color, g.rows.length)}
          {g.rows.map((r) => <Row key={r.nct} r={r} />)}
        </React.Fragment>
      ))}
      {!activeAll.length && <div className="g-empty meta">No trials with upcoming dates.</div>}
      {pastRows.length > 0 && (
        <>
          {grpHead('Completed before today', undefined, pastRows.length, (
            <button type="button" className="linkbtn" aria-expanded={showPast} onClick={() => setShowPast(!showPast)}>{showPast ? 'Hide' : 'Show'}</button>
          ))}
          {showPast && pastRows.map((r) => <Row key={r.nct} r={r} pastRow />)}
        </>
      )}
      {undated.length > 0 && (
        <>
          {grpHead('No completion date stated', undefined, undated.length)}
          {undated.map((r) => <Row key={r.nct} r={r} />)}
        </>
      )}
      <p className="note">Bars run from study start to primary completion (diamond) and are coloured by ClinicalTrials.gov status; the thin tail continues to final completion. Catalysts come from the Ruby Catalyst Calendar: filled markers have happened (colour = outcome), hollow ones are expected. Hover a bar or marker for details.</p>
    </div>
  );
};

/* ---------- By quarter ---------- */
const Quarters: React.FC<{ rows: ArTimelineRow[] }> = ({ rows }) => {
  const now = new Date();
  const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  const qOf = (d: Date) => `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
  const past: ArTimelineRow[] = [], undated: ArTimelineRow[] = [];
  const fut = new Map<number, { label: string; rows: ArTimelineRow[] }>();
  const pcOf = (r: ArTimelineRow) => rowDates(r).pc;
  for (const r of rows) {
    const d = pcOf(r);
    if (!d) undated.push(r);
    else if (d < qStart) past.push(r);
    else { const k = d.getFullYear() * 4 + Math.floor(d.getMonth() / 3); if (!fut.has(k)) fut.set(k, { label: qOf(d), rows: [] }); fut.get(k)!.rows.push(r); }
  }
  const curK = now.getFullYear() * 4 + Math.floor(now.getMonth() / 3);
  const item = (r: ArTimelineRow) => (
    <div
      key={r.nct}
      className="qitem"
      tabIndex={0}
      role="link"
      aria-label={`${r.trial || r.nct}. Open on ClinicalTrials.gov`}
      style={r.nct ? { cursor: 'pointer' } : undefined}
      onClick={() => { if (r.nct) window.open(`https://clinicaltrials.gov/study/${r.nct}`, '_blank', 'noopener'); }}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && r.nct) { e.preventDefault(); window.open(`https://clinicaltrials.gov/study/${r.nct}`, '_blank', 'noopener'); } }}
    >
      <span className="cap" style={{ color: `var(--t${r.tier})` }}>{TIER_NAME[r.tier]}</span>
      <b>{r.trial || r.nct || 'Unnamed trial'}</b>{r.sponsor}<br />
      <span className="note">{[r.phase || 'Phase NR', r.reg?.enrollment != null ? `N=${Number(r.reg.enrollment).toLocaleString()}` : null].filter(Boolean).join(', ')}</span><br />
      <span className={`s st-${r.st}`}>{ST_LABEL[r.st]}</span>
      {r.reg && <span className="rs" style={{ marginLeft: 6 }}>{regLabel(r.reg.status)}</span>}
    </div>
  );
  const col = (label: string, list: ArTimelineRow[], cls?: string) => (
    <section key={label} className={`qcol${cls ? ' ' + cls : ''}`}><h4>{label}<span>{list.length}</span></h4><div className="qlist">{list.map(item)}</div></section>
  );
  return (
    <div className="qcols">
      {past.length > 0 && col(`Before ${qOf(now)}`, past.slice().sort((a, b) => (pcOf(b)?.getTime() || 0) - (pcOf(a)?.getTime() || 0)))}
      {[...fut.keys()].sort((a, b) => a - b).map((k) => col(fut.get(k)!.label, fut.get(k)!.rows.slice().sort((a, b) => (pcOf(a)?.getTime() || 0) - (pcOf(b)?.getTime() || 0)), k === curK ? 'now' : ''))}
      {undated.length > 0 && col('No date stated', undated)}
    </div>
  );
};

export default TimelineTab;
